
/**
 * Product Vue Component
 * ------------------------------------------------------------------------------
 * Main template in sections/product.liquid
 * 
 * My sincerest apologies to retainer :(
 * 
 */

 import Vue from 'vue';
 import axios from 'axios';
 import Slick from 'vue-slick';
 import { getUrlWithVariant } from '@shopify/theme-product-form';
 
 import ReadMore from './read-more.vue';
 import Accordion from './accordion.vue';
 import Collapsible from './collapsible.vue';
 import ButtonSpinner from './button-spinner.vue';
 import { fittingBox } from '../mixins/fittingBox.js';
 import prescriptionController from '../mixins/prescriptionController.js';
 
 import { app } from '../app.js';
 import { xhrRequestConfig, getOnionUrl, getSiblingEndpointUrl } from '../utils/api.js';
 import { removeParameterFromUrl, showElement, hideElement } from '../utils/helpers.js';
 
 import productKeys from '../utils/productKeys.js';
 
 const SIBLING_URL = getSiblingEndpointUrl();
 
 export default {
     mixins: [
         fittingBox,
         prescriptionController()
     ],
 
     components: {
         Slick,
         ReadMore,
         Accordion,
         Collapsible,
         ButtonSpinner
     },
 
     props: {
         productHandle: {
             type: String,
             required: true
         },
         isProductPage: {
             type: Boolean,
             default: false
         },
         showAltPayment: {
             type: Boolean,
             default: false
         },
         messageSuccess: {
             type: String,
             default: "Products Added to Cart"
         },
         messageUploadType: {
             type: String
         },
         messageUploadSize: {
             type: String
         },
         messageUploadSuccess: {
             type: String
         },
         messageUploadError: {
             type: String
         },
         rawFileUrl: {
           type: String,
           default: null
         },
         buttonTextData: {
             type: Object
         }
     },
 
     data() {
         return {
             // General/Misc
             currentSlide: 0,
             currentVariant: null,
             isLoading: false,
             isCartLoad: false,
             mainSlickOptions: {
                 slidesToShow: 1,
                 focusOnSelect: true,
                 asNavFor: '.gallery__nav'
             },
             message: {
                 show: false,
                 message: this.messageSuccess,
                 type: "success"
             },
             parsedFileUrl: '',
 
             // FittingBox
             productVirtualTryOn: {
                 exists: false,
                 glassesID: null
             },
 
             // Product
             options: [],
             product: null,
             siblings: null,
 
             // Form
             isSubmitting: false,
             quantity: "1",

             // Contact Lens Dual-eye
             samePrescription: false,
             rightQuantity: 1,
             leftQuantity: 1,
             rightContactLensOptions: [],
             leftContactLensOptions: []
         }
     },
 
     computed: {
         /////////////////////////////////////        
         // Tags/Badges
         isBlueLightGlass() {
            if (this.product) {
                return this.product.tags.some(tag => tag === productKeys.BLUE_LIGHT)
            }
            return false
         },
         isOptionProduct() {
             return this.product?.type?.toLowerCase() === 'prescription app'
         },
         isGiftCard() {
             return this.product?.type?.toLowerCase().includes('gift card')
         },
         isSale() {
             return this.product?.compare_at_price > this.product?.price;
         },
         isOnlineOnly() {
             if (this.product) {
                 return this.product.tags.find( e => e.toLowerCase() === `${productKeys.BADGE_KEY}|${productKeys.ONLINE_ONLY_KEY}`)
             }
             return false
         },
         isPrescriptable() { // does product have a prescription tag/badge, if not then we dont do anything
             return this.productTags ? 
                 !!this.productTags.find( e => e.value.toLowerCase() === productKeys.PRESCRIPTIBLE_KEY )
                 : 
                 false;
         },
         productTags() {
             return (!!this.product && !!this.product.tags) ? this.product?.tags.map( e => {
                 let tempVal = e.split(window.commonSeparator);
                 return tempVal.length > 1 ? 
                     {
                         "key": e.split(window.commonSeparator)[0].toLowerCase(),
                         "value": e.split(window.commonSeparator)[1].toLowerCase()
                     } : false;
             }).filter(e => e !== false) : false;
         },
         hasSaleBadge() {
             if (!this.productTags) return false;
             let tag = this.productTags.find( tag => tag.key === 'is_sale' );
             if (!!tag) {
                 return tag.value === 'true';
             } else {
                 return false;
             }
         },
         badges() { // array of product badge/tags/flags
 
             return this.productTags ?
             
             this.productTags
                 // get all the tags with the badge key...
                 .filter( badge => badge.key === productKeys.BADGE_KEY )
                 
                 // ... then map them into a nice array
                 .map( e => e.value.toLowerCase() )
                 
                 // ... then remove all the blank, null or 'empty' values
                 .filter( badge => {
                     if (!!badge && badge !== 'empty') {
                         return true
                     }
                     else {
                         return false;
                     }
                 })
 
                 // ... then remove Online Only badges, we show it in a different place
                 .filter( badge => badge !== 'online only')
 
                 // ... then filter out the ones we shouldn't be showing based on the product type
                 .filter( badge => {
                     if (!this.product.type) return true; // if no type is set then fall through
                     
                     let productType = this.product.type.replace(' ', '_').toLowerCase(),
                         output = true;
 
                     // Badges that are only shown for Sunglasses products
                     if (productType !== 'sunglasses') {
                         if (
                             badge === 'prescriptible'
                             ||
                             badge === 'polarised'
                         ) output = false;
                     }
 
                     // Badges that are only shown for Glasses Frames or Sunglasses products 
                     if (productType === 'contact_lenses') {
                         if (
                             badge === 'asian fit'
                         ) output = false;
                     }
 
                     return output;
                 })
             : 
             [];
         },
         productModelByTag() {
             if (!this.productTags) return false;
             if (!!this.isGiftCard) {
                 return this.product.title;
             } else {
                 let tag = this.productTags.find( tag => tag.key === productKeys.MODEL_KEY );
                 if (!!tag) {
                     return tag.value;
                 } else {
                     return this.product?.variants[0].sku || false
                 }
             }
         },
 
         isContactLens() {
             if (!this.product) return false;
             return this.product.type.toLowerCase().includes('contact')
         },
         
         contactLensSubFormValid() {
             if (!this.contactLensOptions.length) return false;
             
             let isValid = [
                 ...this.contactLensOptions.map( option => {
                     if (!option.required) return true;
                     return option.selected === 'null' ? false : !!option.selected;
                 })
             ];
 
             // check every value is not NULL or FALSE
             return isValid.every( item => !!item );;
         },

         rightQuantityParsed() {
             let quantityParsed = parseInt(this.rightQuantity);
             return !!quantityParsed && quantityParsed > 0 ? quantityParsed : false;
         },
         leftQuantityParsed() {
             let quantityParsed = parseInt(this.leftQuantity);
             return !!quantityParsed && quantityParsed > 0 ? quantityParsed : false;
         },
         rightContactLensSubFormValid() {
             if (!this.rightQuantityParsed) return true;
             if (!this.rightContactLensOptions?.length) return false;

             let isValid = [
                ...this.rightContactLensOptions.map(option => {
                    if (!option.required) return true;
                    return !!option.selected;
                })
             ];

             return isValid.every(item => !!item);
         },
         leftContactLensSubFormValid() {
             if (!this.leftQuantityParsed) return true;
             if (this.samePrescription) return this.rightContactLensSubFormValid;
             if (!this.leftContactLensOptions?.length) return false;

             let isValid = [
                ...this.leftContactLensOptions.map(option => {
                    if (!option.required) return true;
                    return !!option.selected;
                })
             ];

             return isValid.every(item => !!item);
         },
         contactLensDualEyeCanAdd() {
             if (!this.isContactLens || !this.prescriptionData) return false;
             if (!this.currentVariant || !this.currentVariant.available) return false;

             const rightActive = !!this.rightQuantityParsed;
             const leftActive = !!this.leftQuantityParsed;
             if (!rightActive && !leftActive) return false;

             if (rightActive && !this.rightContactLensSubFormValid) return false;
             if (leftActive && !this.leftContactLensSubFormValid) return false;
             return true;
         },
 
         /////////////////////////////////////
         // General
         totals() {
 
             if (!!this.isGiftCard) {
                 return {
                     "total": this.currentVariant.price,
                     "options": 0,
                     "savings": 0
                 }
             } else {
                 return {
                     "total": this.product && this.prescriptionData ? this.prescriptionSelected.reduce( (acc, val) => {
                         return acc + val.reduce( (acc, val) => acc + (val.price ? val.price : 0), 0);
                     }, this.product.price) : this.product.price,
                     "options": this.product && this.prescriptionData ? this.prescriptionSelected.reduce( (acc, val) => {
                         return acc + val.reduce( (acc, val) => acc + (val.price ? val.price : 0), 0);
                     }, 0) : 0,
                     "savings": this.product?.compare_at_price ? this.product.compare_at_price - this.product.price : 0
                 }
             }
         },
         buttonText() {
             if (!this.product) return this.buttonTextData.generic;
             
             let type = this.product.type;
             
             if (type.toLowerCase().includes('sunglass')) {
                 return this.buttonTextData.sunglasses;
             }
             else if (type.toLowerCase().includes('contact')) {
                 return this.buttonTextData.contacts;
             } else {
                 return this.buttonTextData.generic;
             }
         },
         quantityParsed() {
             let quantityParsed = parseInt(this.quantity);
             return !!quantityParsed && quantityParsed > 0 ? quantityParsed : false;
         },
         isCartLoading() {
            return app.isCartLoading;
        }
 
     },
     watch: {
        isCartLoading(value) {
             this.isCartLoad = value;
         },
         samePrescription(value) {
             if (value) {
                this._syncLeftContactLensOptionsFromRight();
                this.leftQuantity = this.rightQuantity;
             }
         },
         rightQuantity(value) {
             if (!this.rightQuantityParsed) {
                this.samePrescription = false;
             }
             if (this.samePrescription) {
                this.leftQuantity = value;
             }
         },
         leftQuantity(value) {
             if (!this.leftQuantityParsed) {
                this.samePrescription = false;
             }
             if (this.samePrescription && value !== this.rightQuantity) {
                this.samePrescription = false;
             }
         },
         rightContactLensOptions: {
             deep: true,
             handler() {
                if (this.samePrescription) {
                    this._syncLeftContactLensOptionsFromRight();
                }
             }
        }
     },
 
     created() {
         this.isLoading = true;
 
 
         axios.get(`/products/${this.productHandle}.js`).then((response) => {
             this.product = response.data;
 
             // if the URL has ?variant=[variant.id], select that variant
             let queryStringValues = new URLSearchParams(window.location.search);
             if (queryStringValues.has('variant')) {
                 let variantFromQueryString = this.product.variants.find((variant) => {
                     return variant.id == queryStringValues.get('variant');
                 });
                 if (variantFromQueryString) this.currentVariant = variantFromQueryString;
 
             } else if (this.product.variants.length >= 1) {
                 this.currentVariant = this.product.variants[0];
             }
 
             if (this.currentVariant) {
                 for (let i = 1; i <= this.product.options.length; i++) {
                     this.options.push(this.currentVariant['option' + i]);
                 }
 
                 this.updateVariant();
 
             } else {
                 for (let i = 1; i <= this.product.options.length; i++) {
                     this.options.push('');
                 }
             }
 
         }).catch((error) => {
             console.log(error);
 
         }).then(
             async () => {
                 if (this.isPrescriptable && !!this.product.available) {
                     // If it is prescriptible then get the prescriptions
                     await this._getPrescriptionData(this.currentVariant.id);
                     // then sort out fittingbox VTO
                     this._getTryOnData();
                 } else {
                     // otherwise skip to getting try on data
                     this._getTryOnData();
                 }
                 // always check for siblins
                 this._getSiblings([this.product.id]);
 
                 this._loadPaymentOptions([
                     'https://static.zipmoney.com.au/lib/js/zm-widget-js/dist/zip-widget.min.js'
                 ]);
             }
         );
     },
 
     async mounted() {
         if (this.$refs.main) {
             this.$refs.main.$on('afterChange', this.handleAfterChange);
         }
 
         this.parsedFileUrl = this.rawFileUrl.replace(/[^\/]+$/,'').substring(0, this.rawFileUrl.lastIndexOf('files'));

         const zooomyobserver = new MutationObserver(function () {
            const zooomyArrows = document.querySelector('.zooomyrp-nav')
            if (zooomyArrows) {
                zooomyArrows.style.display = 'block'
                zooomyobserver.disconnect()
            }
         })

         window.addEventListener('load', () => {
            this.readMoreInit()
            zooomyobserver.observe(document, {attributes: false, childList: true, characterData: false, subtree:true})
         })
     },
 
     methods: {
 
         _loadPaymentOptions(scripts) {
             for(var i = 0; i < scripts.length; i++) {
                 if (!!scripts[i]) {
                     let script = document.createElement("script");
                         script.type = "text/javascript";
                         script.src = scripts[i];
                     document.querySelector('body').append(script)
                 }
             }
         },
 
         /////////////////////////////////////
         // General
         handleAfterChange(event, slick, currentSlide) {
             this.currentSlide = currentSlide;
         },
 
         updateVariant() {
             let variant = this._findVariant();
             this._setVariant(variant);
         },
 
         generatedPrescriptionGroupingKey(seed) {
             return `${this.currentVariant.id}-options-${Math.floor( Math.random() * Date.now() )}-${seed}`
         },
 
         generatedUniqueId() {
             return `${Math.floor(Math.random() * Math.floor(Math.random() * Date.now()))}`; // Adding a unique ID to force Shopify to list this as a separate line item
         },
 
         // updateQuantity(quantity) {
         //     if(!quantity == 0 || !parseInt(quantity) == 0 ) {
         //         this.quantity = quantity;
         //     }
         // },
 
         getTagByKey(key) {
             return !!this.productTags ? this.productTags.find( tag => tag.key === key) || false : false;
         },
 
         generateGiftCardUrl(variantId) {
             return `${ this.product.url }?variant=${ variantId }`
         },
 
         _generateCartAddData(includePrescription) {
             let optionProducts = [],
                 customOptions = [],
                 baseProduct = null;
                 if (!!this.isGiftCard) {
                     baseProduct = {
                         quantity: 1, // force qty of 1, duplicate product x selected quantity
                         id: this.currentVariant.id,
                         properties: {
                             '_uniqueID': this.generatedUniqueId(), // Adding a unique ID to force Shopify to list this as a separate line item
                             
                             '_modelName': this.productModelByTag, // store model in tag so we can use it in the cart.data easily,
 
                             '_baseCompareAtPrice': this.currentVariant.compare_at_price || null,
                             '_basePrice': this.currentVariant.price, // original/base price of the product - used for lens pricing app
                             '_totalOptions': 0, // total amount of any selected iption
                             '_totalSavings': 0, // all savings for this product, including that of any child/option products
                             '_totalPrice': this.currentVariant.total, // New Total Price - used for checkout, mini cart, cart
                             '_shownPrice': this.currentVariant.total, // price that is shown to the customer
                         }                        
                     }
                 } else {
                     baseProduct = {
                         quantity: 1, // force qty of 1, duplicate product x selected quantity
                         id: this.currentVariant.id,
                         properties: {
                             '_uniqueID': this.generatedUniqueId(), // Adding a unique ID to force Shopify to list this as a separate line item
                             
                             '_modelName': this.productModelByTag, // store model in tag so we can use it in the cart.data easily,
 
                             '_baseCompareAtPrice': this.currentVariant.compare_at_price || null,
                             '_basePrice': this.product.price, // original/base price of the product - used for lens pricing app
                             '_totalOptions': this.totals.options, // total amount of any selected iption
                             '_totalSavings': this.totals.savings, // all savings for this product, including that of any child/option products
                             '_totalPrice': this.totals.total, // New Total Price - used for checkout, mini cart, cart
                             '_shownPrice': this.totals.total, // price that is shown to the customer
                         }                        
                     };
                 }
 
             // get the contact lens options if any are present
             if (!!this.contactLensOptions) {
                 customOptions = this.contactLensOptions.reduce( (acc, val, index) => {
                     // only return if it has a value set
                     if (val.selected) {
                         return {
                             ...acc, 
                             [`${index + 1}|${val.label}`]: val.selected,
                             [`_option${index + 1}`]: `code:${val.id}|value:[${val.label}:${val.selected}]`
                         }
                     };
                     // otherwise just return the accumulator and ignore
                     return { ...acc };
                 }, {});
 
                 baseProduct.properties = {
                     ...baseProduct.properties,
                     ...customOptions
                 };
             }
 
             // If we are submitting via the Prescription workflow, add all the prescription options
             if (includePrescription) {
                 let prescriptionOptions = {},
                     prescriptionOptionGroup = this.generatedPrescriptionGroupingKey(1);
 
                 // Iterate over each of the selected options and merge them together into a happy format for adding as Line Item Properties
                 // Also, if there is a product ID associated with the option, we need to add it to the cart as well.
                 prescriptionOptions = this.prescriptionSelected.reduce( (acc, step, index) => {
 
                     // Go over all of the children in each step
                     let output = step.reduce( (stepAcc, val) => {
                         
                         // Set up the Keys and Values
                         // LABEL is the values that are shown to users in the cart/minicart/checkout
                         // CODE is the values that the Middleware/Backend cares about
                         let keyLabel = `${index + 1}|${this.prescriptionData.steps[index].title}`,
                             keyCode = '_step'+(index + 1),
                             output = {
                                 [keyLabel]: null,
                                 [keyCode]: null // code:code_code|value:[field:value,field:value]
                             };
 
                             // If option type is FORM, we need to get the sub-form data...
                             if (val.type && val.type === 'form') {
 
                                 let subFormData = 
                                 // Mash all the sub form values into one thing
                                 Object.values(val.selectedChildren).reduce( (subFormOptionsAcc, subFormOptions) => {
                                     return {
                                         ...subFormOptionsAcc,
                                         ...subFormOptions
                                     }
                                 }, {});
 
                                 // then strip out the unset/null values
                                 subFormData = Object.entries(subFormData).filter(elem => elem[1]);
 
                                 // Need some special formatting for prescription upload
                                 if (val.option_code === productKeys.UPLOAD_PRESCRIPTION_FIELD_CODE) {
                                     output[keyLabel] = subFormData[0][1].split('|||')[0];
                                     // output[keyLabel] = `Uploaded file: ${this.fileUpload.fileName}`;
                                     output[keyCode] = `code:${val.option_code}|value:[file_name:${subFormData[0][1].split('|||')[1]}]`;
                                     output['_step2-prescription-file'] = this.fileUpload.fileUrl;
 
                                 } else {
 
                                     let temp = subFormData.map( elem => {
                                         // split to capitalise, then join by spaces
                                         let temp = elem[0].split('_').map( elemChild => elemChild[0].toUpperCase() + elemChild.slice(1) ).join(' ');
                                         return `${ temp }: ${elem[1]}`;
                                     });
 
                                     if (!!temp.length) {
                                         output[keyLabel] = [
                                             val.title,
                                             temp.join(', ')
                                         ].join(', ');
                                     } else {
                                         output[keyLabel] = val.title
                                     }
 
                                     output[keyCode] = `code:${val.option_code}|value:[${subFormData.map( elem => elem.join(':')).join(',')}]`
                                 }
                             } 
                             // ...otherwise its just a normal option
                             else {
                                 // except if it is email_prescription, then we get the description instead
                                 let value = val.option_code === productKeys.EMAIL_PRESCRIPTION_FIELD_CODE ? val.description : val.title
                                 // if it has a price add that too
                                 output[keyLabel] = val.price ? `${val.title} - ${ Intl.NumberFormat('en-AU', {style: 'currency',currency: 'AUD',minimumFractionDigits: 2}).format(val.price / 100) }` : `${value}`;
                                 output[keyCode] = `code:${val.option_code}`
                             }
 
                             // Lastly, check if the keys exist already in the Accumulator
                             // and, if so, prepend the existing values
                             if (stepAcc[keyLabel]) output[keyLabel] = stepAcc[keyLabel] + ', ' + output[keyLabel];
                             if (stepAcc[keyCode]) output[keyCode] = stepAcc[keyCode] + ',' + output[keyCode];
 
                             // If the option includes a variant ID, it is an option product and we need to add it to the cart
                             if(!!val.shopify_variant_id) {
                                 let optionProduct = {
                                     quantity: 1,
                                     id: val.shopify_variant_id,
                                     properties: {
                                         '_prescriptionOptionGroup': prescriptionOptionGroup, // Assign the previously generated option group to this product
                                         '_uniqueID': this.generatedUniqueId(), // Adding a unique ID to force Shopify to list this as a separate line item
                                         '_totalPrice': val.price, // New Total Price - used for checkout, mini cart, cart
                                         '_totalSavings': 0,
                                         '_totalOptions': 0,
                                         '_basePrice': val.price, // orig price - used for lens pricing app
                                         '_optionProduct': true
                                     }          
                                 };
 
                                 baseProduct.properties._shownPrice += val.price;
 
                                 optionProducts.push(optionProduct);
                             };
                         
                         return { 
                             // Add the existing accumulator. We need it for multiple choice options, so subsequent siblings can append values
                             ...stepAcc,
                             // Add the new key/value pairs
                             ...output
                         }
                     }, {});
 
                 return { 
                         ...acc,
                         ...output
                     };
                 }, {});
 
 
                 baseProduct.properties = {
                     ...baseProduct.properties,
                     ...prescriptionOptions,
                     '_prescriptionOptionGroup': prescriptionOptionGroup, // Assign the previously generated option group to this product
                 };
             };
 
             let tempItems = [
                 baseProduct,
                 ...optionProducts
             ];
 
             /*
                 if the quantity is MORE than 1, we need to:
                 - duplicated the items (base product and option products)
                 - if there is option products, update each set of products with a unique prescriptionOptionGroup, so they are unique sets
                 - flatten the array, and pass that back as the products to add to cart
             */ 
             if (this.quantityParsed > 1) {
                 let updatedTempItems = [];
 
                 for (let i = 0; i < this.quantityParsed; i++) {
                     // clone item
                     let temp = tempItems.map( (item) => JSON.parse(JSON.stringify(item)) );
 
                     
                         var updatedPrescriptionOptionGroup = this.generatedPrescriptionGroupingKey(i);
 
                         temp = temp.map( item => {
                             let updatedItem = { ...item };
                                 updatedItem.properties._uniqueID = this.generatedUniqueId();
 
                                 if (!!includePrescription) {
                                     updatedItem.properties._prescriptionOptionGroup = updatedPrescriptionOptionGroup;
                                 };
 
                             return updatedItem;
                         });
 
                     updatedTempItems.push(temp);
                 };
 
                 tempItems = updatedTempItems.flat();
             };
 
             return { 
                 'items': tempItems
             };
             
         },

         _showContactLensInvalidPopup() {
             const isVisible = (el) => !!el && el.offsetParent !== null;
             const popup = (el, message) => {
                if (!el) return;
                el.setCustomValidity(message);
                el.reportValidity();
                const clear = () => el.setCustomValidity('');
                el.addEventListener('change', clear, { once: true });
                el.addEventListener('input', clear, { once: true });
             };

             // Both qty 0 — popup on the visible qty input
             if (!this.rightQuantityParsed && !this.leftQuantityParsed) {
                const qtyInput = [...this.$el.querySelectorAll('.quantity_container input[type="number"]')].find(isVisible);
                popup(qtyInput, 'Please set quantity for at least one eye');
                return;
             }

             // Active eye(s) with an empty required option — popup on the first visible select
             const eyes = [
                { active: !!this.rightQuantityParsed, options: this.rightContactLensOptions, prefix: 'right' },
                { active: !!this.leftQuantityParsed && !this.samePrescription, options: this.leftContactLensOptions, prefix: 'left' }
             ];
             for (const eye of eyes) {
                if (!eye.active) continue;
                for (const opt of eye.options) {
                    if (!opt.required) continue;
                    if (opt.selected) continue;
                    const selectEl = [...this.$el.querySelectorAll(`[id="${eye.prefix}-${opt.id}"]`)].find(isVisible);
                    popup(selectEl, 'Please select an item in the list.');
                    return;
                }
             }
         },

         _syncLeftContactLensOptionsFromRight() {
             if (!this.rightContactLensOptions?.length || !this.leftContactLensOptions?.length) return;
             const rightById = this.rightContactLensOptions.reduce((acc, opt) => {
                acc[opt.id] = opt;
                return acc;
             }, {});

             this.leftContactLensOptions = this.leftContactLensOptions.map(leftOpt => {
                const rightOpt = rightById[leftOpt.id];
                if (!rightOpt) return leftOpt;
                return {
                    ...leftOpt,
                    selected: rightOpt.selected
                };
             });
         },

         _generateContactLensCartAddDataDualEye(includePrescription) {
             const buildEyeBase = (eye, options) => {
                const props = {
                    '_uniqueID': this.generatedUniqueId(),
                    '_modelName': this.productModelByTag,
                    '_baseCompareAtPrice': this.currentVariant.compare_at_price || null,
                    '_basePrice': this.product.price,
                    '_totalOptions': this.totals.options,
                    '_totalSavings': this.totals.savings,
                    '_totalPrice': this.totals.total,
                    '_shownPrice': this.totals.total
                };

                if (options?.length) {
                    options.forEach((val, index) => {
                        if (val.selected) {
                            props[`${index + 1}|${val.label}`] = val.selected;
                            props[`_option${index + 1}`] = `code:${val.id}|value:[${val.label}:${val.selected}]`;
                        }
                    });
                }

                if (includePrescription) {
                    props['_prescriptionOptionGroup'] = this.generatedPrescriptionGroupingKey(eye === 'right' ? 1 : 2);
                }

                return {
                    quantity: 1,
                    id: this.currentVariant.id,
                    properties: props
                };
             };

             const buildItemsForEye = (eye, options, qtyParsed) => {
                if (!qtyParsed || qtyParsed < 1) return [];
                const base = buildEyeBase(eye, options);
                const out = [];
                for (let i = 0; i < qtyParsed; i++) {
                    const copy = JSON.parse(JSON.stringify(base));
                    copy.properties._uniqueID = this.generatedUniqueId();
                    if (includePrescription) {
                        copy.properties._prescriptionOptionGroup = this.generatedPrescriptionGroupingKey(`${eye}-${i}`);
                    }
                    out.push(copy);
                }
                return out;
             };

             const items = [
                ...buildItemsForEye('right', this.rightContactLensOptions, this.rightQuantityParsed),
                ...buildItemsForEye('left', this.samePrescription ? this.rightContactLensOptions : this.leftContactLensOptions, this.leftQuantityParsed)
             ];

             return { items };
         },
 
         async handleAddToCart(includePrescription = false) {

             if (!this.currentVariant) return;

             // Single validation gate for contact-lens dual-eye
             if (this.isContactLens && this.prescriptionData && !this.contactLensDualEyeCanAdd) {
                 this._showContactLensInvalidPopup();
                 return;
             }

             if (this.currentVariant) {
                 this.isSubmitting = true;

                let data = null;
                if (this.isContactLens && (this.rightQuantityParsed || this.leftQuantityParsed)) {
                    data = this._generateContactLensCartAddDataDualEye(includePrescription);
                } else {
                    data = this._generateCartAddData(includePrescription);
                }
                 
                 // Not reusing App.js addToCart method here so we can get the response/message data to show in the page
                 await axios.post('/cart/add.js', data, xhrRequestConfig).then(async (response) => {
                     await app.getCart(true);
                     app.isMinicartOpen = true;
                     this.message = {
                         show: true,
                         message: `You added ${(!!this.productModelByTag ? (this.product.vendor + ' - ' + this.productModelByTag) : this.product.title )} to your shopping cart`,
                         type: "success"
                     };
                 }).catch((error) => {
                     this.message = {
                         show: true,
                         message: error.response.data.description,
                         type: "error"
                     };
                 }).then( () => {
                     // Not using the toggle here, need to force it to close in case the 
                     // user clicked the regular add button w/o prescription options
                     this.prescriptionWorkflowVisible = false;
                     this.clearSelectedPrescription('all');
                 })
 
                 this.isSubmitting = false;
             }
         },
 
         // When product has multiple options, we need to find the associated variant based on those options
         _findVariant() {
             for (let i = 0; i < this.product.variants.length; i++) {
                 if (this.product.variants[i].option1 && this.product.variants[i].option1 != this.options[0]) continue;
                 if (this.product.variants[i].option2 && this.product.variants[i].option2 != this.options[1]) continue;
                 if (this.product.variants[i].option3 && this.product.variants[i].option3 != this.options[2]) continue;
 
                 return this.product.variants[i];
             }
 
             return null;
         },
 
         _setVariant(variant) {
 
 
             if (!variant) {
                 this.currentVariant = null;
                 removeParameterFromUrl('variant');
                 return;
             }
 
             
             if (!!this.isGiftCard) {
                 this.currentVariant = variant;
             } else {
                 this.currentVariant = {
                     ...variant,
                     price: this.product.price,
                     compare_at_price: this.product.compare_at_price,
                 };
             }
 
             if (this.isProductPage) {
                 if (variant.featured_image && this.product.images.length > 1)
                     this.$refs.main.goTo(variant.featured_image.position - 1);
 
                 let url = getUrlWithVariant(window.location.href, variant.id);
                 window.history.replaceState({path: url}, '', url);
             }
         },
 
         async _getTryOnData() {
 
             if (!!this.product) {
                 let vtoTag = this.product.tags.find(tag => tag.includes('fittingbox_vto')),
                     vtoValue = !!vtoTag ? (vtoTag.split('|')[1].toLowerCase() ===  'true') : false,
                     glassesIdTag = this.product.tags.find(tag => tag.includes('fittingbox_glassesid')),
                     glassesIdValue = !!glassesIdTag ? glassesIdTag.split('|')[1].toLowerCase() : null;
                 
                 this.productVirtualTryOn.exists = vtoValue;
                 this.productVirtualTryOn.glassesID = glassesIdValue;
 
                 if (!!this.productVirtualTryOn.exists) {
                     await this._initTryOn();
                 }
             }
             
             this.isLoading = false;
         },
         
         async _getSiblings() {
 
            //  let requestData = 
            //  {
            //      "filter":{
            //          "associate_by_group_tag": "model",
            //          "in_stock": true,
            //          "collections": [ "all" ],
            //          "tag__model": [ this.productModelByTag.toUpperCase() ]
            //      }
            //  };
            let productSiblingsUrl = SIBLING_URL.replace('[PRODUCT_ID]', this.product.id)
       
             
             axios.get(productSiblingsUrl).then( ({ data }) => {
                     let siblingData = null;
                     if (data) {
                       
                         siblingData = data.data.map( sibling => {
                            console.log('prod sib', sibling);
                             let modelTag = sibling.tags.find( tag => tag.toLowerCase().split('|')[0] === 'model'),
                                 modelTagData = modelTag ? (
                                     modelTag.split('|')[1].replaceAll('_', ' ').toLowerCase()
                                 ) : '',
                                 
                                 colourDescriptionTag = sibling.tags.find( tag => tag.toLowerCase().split('|')[0] === 'colour_description'),
                                 colourDescriptionTagData = colourDescriptionTag ? (
                                     colourDescriptionTag.split('|')[1].replaceAll('_', ' ').toLowerCase()
                                 ) : '',
 
                                 colourFamilyTag = (sibling.tags.find( tag => tag.toLowerCase().split('|')[0] === 'colour_family') || colourDescriptionTag),
                                 colourFamilyTagData = colourFamilyTag ? (
                                     colourFamilyTag.split('|')[1].replaceAll(' ', '-').toLowerCase()
                                 ) : '',
                                 output = {
                                     ...sibling,
                                     'title': `${modelTagData} ${colourDescriptionTagData}`,
                                     'swatchUrl': this.parsedFileUrl + "files/colour-swatch-" + colourFamilyTagData + ".jpg",
                                     'url': `/products/${sibling['handle']}`
                                 };
 
                             return  !!colourFamilyTagData ? output : false;
                         })
                         .filter(sibling => !!sibling);
                     };
             
 
                     this.siblings = siblingData;
 
                 });
             },
 
             toggleDescriptionPopup(key) {
                 let newVal = !this.stepPopups[key];
                 Vue.set(this.stepPopups, key, newVal);
             },
 
             /////////////////////////////////////
             // contact lens option fields
             /* 
                 reformat the prescription options for contact lenses so they are happy
                 We assume that the contact lens options follow the same format as live:
                 - one set of values per option
                 - we only cater for text or select fields
             */
             _generateContactLensOptions() {
                 let options = this.prescriptionData.steps.map( step => {
                     return step.options.map( option => {
                         return {
                             "order": option.sort_order,
                             "id": option.option_code,
                             "label": option.description,
                             "selected": '',
                             // assume that there is only one set of options for each step > option, so we can get a bit more specific with what we are taking
                             "required": option.options.fields[0].required,
                             "values": option.options.fields[0].values,
                             "type": option.options.fields[0].type,
                         }
                     })
                 }).flat(1);
 
                 // order the data
 
                const sorted = options.sort( (a, b) => {
                     if (a < b) {
                         return -1;
                     }
                     // if (a > b) { // can fall back to this, dont need the logic
                     return 1;
                     // }
                 });

                sorted.forEach(opt => {
                    // if an option only has a single possible value, auto-select it
                    if (opt.values?.length === 1) {
                        opt.selected = opt.values[0];
                    }
                    if (opt.values?.length && opt.values.every(v => !isNaN(parseFloat(v)))) {
                        opt.values.sort((a, b) => parseFloat(b) - parseFloat(a));
                        const hasNeg = opt.values.some(v => parseFloat(v) < 0);
                        const hasPos = opt.values.some(v => parseFloat(v) > 0);
                        if (hasNeg && hasPos) {
                            opt.hasPowerLayout = true;
                            opt.positiveValues = opt.values.filter(v => parseFloat(v) > 0);
                            opt.nonPositiveValues = opt.values.filter(v => parseFloat(v) <= 0);
                            opt.selected = '';
                        }
                    }
                });

                this.contactLensOptions = sorted;

                this.rightContactLensOptions = JSON.parse(JSON.stringify(sorted));
                this.leftContactLensOptions = JSON.parse(JSON.stringify(sorted));
             },
             updateRightQuantity(delta) {
                const qty = Number(this.rightQuantity);
                const change = Number(delta);
                let newQty = qty + change;
                if (newQty < 0) newQty = 0;
                this.rightQuantity = newQty;
             },
             updateLeftQuantity(delta) {
                const qty = Number(this.leftQuantity);
                const change = Number(delta);
                let newQty = qty + change;
                if (newQty < 0) newQty = 0;
                this.leftQuantity = newQty;
             },
             updateQuantity(delta) {
                const qty = Number(this.quantity); 
                const change = Number(delta);       
                let newQty = qty + change;
              
                if (newQty < 1) newQty = 1;
                this.quantity = newQty;
              },
             readMoreInit() {
                const brandsInfoContentContainer = document.querySelector('.brands-info__content-container')
                if (brandsInfoContentContainer) {
                    const readMoreBtn = document.querySelector('.read-more')
                    const heightThreshold = parseFloat(window.getComputedStyle(brandsInfoContentContainer).lineHeight.replace('px', '')) * 4
                    if (brandsInfoContentContainer.scrollHeight > heightThreshold) {
                        brandsInfoContentContainer.style.height = `${heightThreshold}px`
                        readMoreBtn.classList.remove('d-none')
                        readMoreBtn.classList.add('d-block')
                    }
                }
             },
             handleReadMore (e) {
                const brandsInfoContentContainer = document.querySelector('.brands-info__content-container')
                const readLessBtn = document.querySelector('.read-less')
                hideElement(e.target.closest('.read-more'))
                showElement(readLessBtn)
                brandsInfoContentContainer.style.height = `${brandsInfoContentContainer.scrollHeight}px`
             },
             handleReadLess (e) {
                const brandsInfoContentContainer = document.querySelector('.brands-info__content-container')
                const heightThreshold = parseFloat(window.getComputedStyle(brandsInfoContentContainer).lineHeight.replace('px', '')) * 4
                const readMoreBtn = document.querySelector('.read-more')
                hideElement(e.target.closest('.read-less'))
                showElement(readMoreBtn)
                brandsInfoContentContainer.style.height = `${heightThreshold}px`
             }
         },
 };